/*
 * Copyright (c) 2023-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {afterNextRender, Directive, inject, Injector, signal} from '@angular/core';
import {
    CellClassRules,
    CellEditingStoppedEvent,
    ColDef,
    ColGroupDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    RowClassRules,
    RowNode
} from 'ag-grid-community';
import {EsciComponent} from './esci.component';
import {FieldRule, ValidationModel} from '../model/validation.model';
import {
    CustomMultiSelectFloatingFilterComponent
} from './filter-components/custom-multi-select-filter/custom-multi-select-floating-filter.component';
import {TypeaheadCellEditorComponent} from './cell-renderer/typeahead-cell-editor.component';
import {CommonHelperService} from '../service/common-helper.service';
import {CustomNoRowsOverlayComponent} from './custom-no-rows-overlay-component';
import {refreshCellAndDeferredRowRedraw} from './cell-renderer/prime-cell-editor-tab.util';
import {isRowWriteAllowedByMetadata} from '../utils/row-write-control.util';

@Directive()
export class EditableGridComponent extends EsciComponent {
    private readonly dropdownAllowedValuesCache = new WeakMap<object, {
        allowedValuesRef: any[],
        allowedValueKey?: string,
        normalizedValues: Set<string>
    }>();
    protected readonly injector = inject(Injector);
    // most pages will just have one grid, so this is that api reference
    gridApi: GridApi;
    // Some pages like kts may have multiple grids in a map
    gridApiMap: Map<string, GridApi> = new Map<string, GridApi>();
    // Some pages have a modal with a grid
    modalGridApi: GridApi;
    cols: (ColDef | ColGroupDef)[] = [];
    // key for the map above in kts pages
    version: string;
    // this is something each component should define separately for their save endpoint
    saveUrl: string;
    // these booleans dictate the ui buttons and data validation
    saving = false;
    exporting = false;
    loading = true;
    resetting = false;
    dataChanged = false;
    rowData: ValidationModel[] = [];
    readonly rowDataSignal = signal<any[]>([]);
    duplicate = false;
    rowsInvalid = false;
    persistentUserData: any;
    cellClassRules: CellClassRules = {
        // Derived group child columns (e.g. "Similar Ring Terminals" -> Supplier) reuse the real
        // member's field name for value resolution, so they must be excluded here or they'd inherit
        // the "changed" highlight whenever the unrelated real column with that field name is edited.
        'cell-changed': params => !(params.colDef as any)?.esciDerivedGroupChildField
            && params.data.changes?.get(params.colDef.field),
        'cell-error': params => !!this.getCellValidationMessage(params?.data, params?.colDef)
    };
    // default column definition, overridden by any pages that don't want this
    defaultColDef: ColDef = {
        floatingFilter: true,
        resizable: true,
        sortable: true,
        filter: true,
        minWidth: 140,
        // this way all columns are editable by default as long as there is no admin check or they are an admin
        // some pages only have one permission check, some pages have users and admins
        // PURCH_ADMIN users can edit PPCO fields even without full admin permission.
        // COMPONENT_TIER_N_ADMIN users (and only those users) can edit tierNResilient fields,
        // even when they have COMPONENT_ADMIN — the Tier-N gate is not relaxed by general admin.
        editable: (params) => {
            const field = params?.colDef?.field;
            // Tier-N Resilient: only COMPONENT_TIER_N_ADMIN may edit, regardless of any other role.
            // This gate must run BEFORE the hasFullPermission short-circuit so a Bracket/Wire/etc.
            // admin without COMPONENT_TIER_N_ADMIN does not get write access to the Tier-N column.
            if (field && (field === 'tierNResilient' || field === 'tierNResilientId' ||
                          field.startsWith('tierNResilient.'))) {
                return this.hasTierNAdminPermission();
            }
            let hasWritePermission = false;
            const hasFullPermission = !this.adminPermissionUrl || !!this.adminPermission();
            if (hasFullPermission) {
                hasWritePermission = true;
            } else if (this.hasPurchAdminPermission() && field && field.startsWith('ppco.')) {
                // Allow PURCH_ADMIN users to edit PPCO fields
                hasWritePermission = true;
            }
            if (!hasWritePermission) {
                return false;
            }
            return isRowWriteAllowedByMetadata(params?.colDef, params?.data);
        },
        cellStyle: (params) => ({
            display: 'flex',
            alignItems: 'center'
        }),
        wrapHeaderText: true,
        autoHeaderHeight: true,
        wrapText: true,
        cellClassRules: this.cellClassRules,
        // Guard against malformed keyboard events (missing string `key`) from third-party editor/focus flows.
        // AG Grid keyboard navigation assumes event.key is always a string and can throw otherwise.
        suppressKeyboardEvent: params => this.shouldSuppressMalformedKeyboardEvent(params?.event as KeyboardEvent | undefined)
    };

    public gridOptions: GridOptions = {
        onColumnMoved: this.onColumnMoved.bind(this),
        overlayNoRowsTemplate: '',
        components: {
            customNoRowsOverlay: CustomNoRowsOverlayComponent
        },
        noRowsOverlayComponent: 'customNoRowsOverlay',
        noRowsOverlayComponentParams: {
            message: this.getDynamicNoRowsMessage()
        },
        // AG Grid's default popupParent is the grid's own root wrapper element. Our grids are
        // rendered inside height-constrained containers (e.g. a fixed-height ag-grid-angular host),
        // so filter/menu popups created against that default parent can render inline within the
        // page layout (visible below the grid) instead of as a correctly positioned floating popup -
        // most noticeably when a filter's component is instantiated programmatically (e.g. via
        // setFilterModel) before the user has ever opened that column's filter menu. Anchoring
        // popups to document.body is AG Grid's documented fix for this class of issue.
        popupParent: document.body
    };

    // Give column IDs or field values for any columns that should only be visible to admins
    adminOnlyColumns = [];
    // pretty standard coloring rules based on changed data
    rowClassRules: RowClassRules = {
        // row style function
        'modified-row': params => params.data.state === 'changed',
        'new-row': params => params.data.state === 'new',
        'deleted-row': params => params.data.state === 'deleted',
        'milestone-row': params => params.data.phase && params.data.phase === 'Milestone'
    };

    private static applyStyleToFilterColumns(index, header) {
        const headerCell = document.querySelector(header)
            .querySelector(`.ag-header-cell.ag-floating-filter.ag-focus-managed:nth-child(${index + 1})`);
        if (headerCell) {
            const div = headerCell.querySelector('div');
            if (div) {
                div.style.display = 'flow';
            } else {
                console.error(`The specified div was not found within the header cell for column index ${index + 1}.`);
            }
        } else {
            console.error(`The specified header cell was not found for column index ${index + 1}.`);
        }
    }

    protected isGridApiAlive(api: GridApi | undefined | null): api is GridApi {
        if (!api) {
            return false;
        }
        return typeof api.isDestroyed !== 'function' || !api.isDestroyed();
    }

    /**
     * ag-grid 31 does not set col-id on floating-filter header cells. Copy col-id from the label
     * row onto filter-row cells at the same index so CSS `.ag-header-row-column-filter [col-id]`
     * rules can tint the filter row.
     */
    protected syncFilterRowColIds(gridHost: HTMLElement | null | undefined): void {
        if (!gridHost) {
            return;
        }
        const filterRow = gridHost.querySelector('.ag-header-row-column-filter');
        const labelRow = gridHost.querySelector('.ag-header-row-column');
        if (!filterRow || !labelRow) {
            return;
        }
        const labelCells = labelRow.querySelectorAll('.ag-header-cell');
        const filterCells = filterRow.querySelectorAll('.ag-header-cell.ag-floating-filter');
        labelCells.forEach((labelCell, index) => {
            const filterCell = filterCells.item(index) as HTMLElement | null;
            if (!filterCell) {
                return;
            }
            const colId = labelCell.getAttribute('col-id');
            if (colId) {
                filterCell.setAttribute('col-id', colId);
            } else {
                filterCell.removeAttribute('col-id');
            }
        });
    }

    // register grid apis as grid appears, for single or multiple
    onGridReady(params: GridReadyEvent, key?: string) {
        if (key) {
            // this is for our tabbed pages like kts
            this.gridApiMap.set(key, params.api);
            params.api.sizeColumnsToFit();
        } else {
            this.gridApi = params.api;
            afterNextRender(() => {
                if (this.gridApi && !this.gridApi.isDestroyed()) {
                    this.gridApi.sizeColumnsToFit();
                }
            }, { injector: this.injector });
            this.afterPrimaryGridReady(params);
        }
        // show overlay if needed
        this.onFilterChanged();
    }

    /**
     * Called after the primary (non-keyed) grid API is assigned. Override to augment the grid
     * (e.g. inject controls into the pagination bar) once {@link gridApi} is set.
     */
    protected afterPrimaryGridReady(_params: GridReadyEvent): void {
        // optional override
    }

    protected reset(): void {
        this.clearEditState();

        // Clear all filters
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.setFilterModel(null);
        }
    }

    /** Clears the loading and pending-edit flags a load settles, without touching the grid's filters. */
    protected clearEditState(): void {
        this.resetting = false;
        this.exporting = false;
        this.loading = false;
        this.dataChanged = false;
    }

    updateActiveFilters(): void {
        // This serves as a hook to override if your component needs to do something when the filters change
    }

    removeSearchTerm(key: string): void {
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        const filterModel = this.gridApi.getFilterModel();
        if (!filterModel) {
            return;
        }

        const newFilterModel: any = {};

        for (const colId in filterModel) {
            const col = this.gridApi.getColumnDef(colId);
            const colName = col?.headerName || colId;

            // Keep all filters except the one we're removing
            if (colName !== key) {
                newFilterModel[colId] = filterModel[colId];
            }
        }

        // Apply updated filter model
        this.gridApi.setFilterModel(Object.keys(newFilterModel).length > 0 ? newFilterModel : null);
    }

    removeAllSearchTerms(): void {
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.setFilterModel(null);
        }
    }

    onFilterChanged(): void {
        if (!this.isGridApiAlive(this.gridApi)) {
            return;
        }
        this.updateActiveFilters();

        const rowCount = this.gridApi.getDisplayedRowCount();
        if (rowCount === 0) {
            // The intention of this section is to show a custom no rows overlay when there are no rows after filtering
            // However, it never properly updates the overlay when the data changes.
            // this.gridApi.hideOverlay();
            // // When the details of this overlay change, we have to hide the overlay and show it again
            // // for the content to be updated, so we always call hideOverlay first
            // setTimeout(() => {
            //     this.gridOptions.noRowsOverlayComponentParams = {
            //         message: this.getDynamicNoRowsMessage()
            //     };
            //     this.gridApi.showNoRowsOverlay(); // re-show with updated params
            // });
            this.gridApi.showNoRowsOverlay(); // re-show with updated params
        } else {
            this.gridApi.hideOverlay();
        }
    }

    protected getDynamicNoRowsMessage(): string {
        return 'No matching data. Try adjusting your filters.';
    }

    onModalGridReady(params: GridReadyEvent) {
        this.modalGridApi = params.api;
        afterNextRender(() => {
            if (this.modalGridApi && !this.modalGridApi.isDestroyed()) {
                this.modalGridApi.sizeColumnsToFit();
            }
        }, { injector: this.injector });
    }

    onColumnMoved(event: any): void {
    }

    getValueFromUser(user: any, field?: string): any {
        if (!user || (field && !user[field])) {
            return '';
        }
        if (field) {
            user = user[field];
        }
        if (user == null) {
            return '';
        }
        if (user.fullName) {
            return user.fullName;
        }
        if (user.firstName || user.lastName) {
            return [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
        }
        // Placeholder / incomplete user objects (e.g. { id: -1 } from API) must not be returned as-is or
        // exports and tooling stringify them; empty selection is represented as blank.
        return '';
    }

    getValueFromUsers(users: any[], field?: string) {
        if (!users || users.length === 0) {
            return '';
        }
        return users.map(user => this.getValueFromUser(user, field)).join(', ');
    }

    setColumnsVisible() {
        this.gridApiMap.forEach(o => {
            if (this.isGridApiAlive(o)) {
                o.setColumnsVisible(this.adminOnlyColumns, !!this.adminPermission());
            }
        });
        if (this.isGridApiAlive(this.gridApi)) {
            this.gridApi.setColumnsVisible(this.adminOnlyColumns, !!this.adminPermission());
        }
    }

    areRowsValid(rowLists: Map<string, Array<ValidationModel>> | ValidationModel[]) {
        this.duplicate = false;
        this.rowsInvalid = false;
        this.messageService.clear();
        if (rowLists instanceof Map) {
            for (const rows of rowLists.values()) {
                this.doValidation(rows);
            }
        } else {
            this.doValidation(rowLists);
        }
        return !this.duplicate && !this.rowsInvalid;
    }

    private doValidation(rowList: Array<ValidationModel>) {
        for (const row of rowList || []) {
            delete (row as any)._cellValidationErrors;
        }
        // Rows marked for deletion are still in rowData until save; exclude them from validation so
        // unique-set checks (and field rules) do not treat them as active duplicates of remaining rows.
        const activeRows = rowList.filter(row => row.state !== 'deleted');
        // For single-row validations (for example row-level save), compare duplicates against the full
        // active grid set so uniqueness constraints still account for other non-deleted rows.
        const shouldUseFullGridForDuplicateCheck = activeRows.length === 1
            && Array.isArray((this as any).rowData)
            && (this as any).rowData.includes(activeRows[0]);
        const duplicateComparisonRows = (shouldUseFullGridForDuplicateCheck
            ? (this as any).rowData
            : rowList).filter(row => row.state !== 'deleted');
        // check for length validation based on column sizes. backend will stop it but should also do it here
        const rowValidationErrors = this.validatorService.collectFieldValidationErrors(activeRows, true);
        // Dropdown "value is not in the allowed list" errors are otherwise only detected by
        // cellClassRules at cell-render time, which AG Grid never evaluates for rows hidden by an
        // active grid filter (e.g. the default developmentStatus/gmApprovalStatus filter). Merging
        // them in here ensures every row is checked in this single pass on load, regardless of filter
        // visibility, and that they are included in the same validateFields() summary popup below.
        this.mergeDropdownAllowedValueErrors(activeRows, rowValidationErrors);
        this.rowsInvalid = this.validatorService.validateFields(activeRows, rowValidationErrors) || this.rowsInvalid;
        for (const [row, cellValidationErrors] of rowValidationErrors.entries()) {
            (row as any)._cellValidationErrors = cellValidationErrors;
            this.rowsInvalid = true;
        }

        for (const row of activeRows) {
            // do any other active rows have the same unique-set values as this row
            if (duplicateComparisonRows.some(r => this.validatorService.checkDuplicate(r, row))) {
                this.duplicate = true;
                break;
            }
        }
    }

    /**
     * Adds a "Value is not in the allowed list." entry into fieldErrors for every row/column where the
     * dropdown value is not among that column's configured allowedValues - independent of whether AG
     * Grid currently renders/filters that row. Skips fields a fieldRule violation already flagged, so
     * the more specific message (required/pattern/maxLength) is never overwritten.
     */
    private mergeDropdownAllowedValueErrors(
        rows: ValidationModel[],
        rowValidationErrors: Map<ValidationModel, Map<string, string>>
    ): void {
        const dropdownCols = this.flattenColumnDefsForValidation(this.cols).filter(col => {
            const allowedValues = col?.cellRendererParams?.allowedValues || col?.cellEditorParams?.allowedValues;
            return !!col.field && Array.isArray(allowedValues) && allowedValues.length > 0;
        });
        if (dropdownCols.length === 0) {
            return;
        }
        for (const row of rows) {
            for (const colDef of dropdownCols) {
                const fieldName = colDef.field as string;
                if (rowValidationErrors.get(row)?.has(fieldName)) {
                    continue;
                }
                if (!this.hasInvalidDropdownSelection(row, colDef)) {
                    continue;
                }
                let fieldErrors = rowValidationErrors.get(row);
                if (!fieldErrors) {
                    fieldErrors = new Map<string, string>();
                    rowValidationErrors.set(row, fieldErrors);
                }
                fieldErrors.set(fieldName, 'Value is not in the allowed list.');
            }
        }
    }

    private flattenColumnDefsForValidation(columns: (ColDef | ColGroupDef)[]): ColDef[] {
        const result: ColDef[] = [];
        for (const col of columns || []) {
            if (col && 'children' in col && Array.isArray((col as ColGroupDef).children)) {
                result.push(...this.flattenColumnDefsForValidation((col as ColGroupDef).children));
            } else {
                result.push(col as ColDef);
            }
        }
        return result;
    }

    protected getCellValidationMessage(row: any, colDef?: ColDef): string | null {
        const fieldName = colDef?.field;
        if (!row || !fieldName) {
            return null;
        }
        const rowErrors = row._cellValidationErrors as Map<string, string> | undefined;
        const standardValidationError = rowErrors?.get(fieldName);
        if (standardValidationError) {
            return standardValidationError;
        }
        if (this.hasInvalidDropdownSelection(row, colDef)) {
            return 'Value is not in the allowed list.';
        }
        return null;
    }

    private hasInvalidDropdownSelection(row: any, colDef: ColDef): boolean {
        const allowedValues = colDef?.cellRendererParams?.allowedValues || colDef?.cellEditorParams?.allowedValues;
        if (!Array.isArray(allowedValues) || allowedValues.length === 0) {
            return false;
        }
        const fieldName = colDef.field;
        if (!fieldName) {
            return false;
        }
        const value = CommonHelperService.getDeepValue(row, fieldName);
        if (value == null || value === '') {
            return false;
        }
        const memberType = colDef?.cellEditorParams?.memberType;
        const allowedValueKey = colDef?.cellEditorParams?.allowedValueKey || colDef?.cellRendererParams?.allowedValueKey;
        const maxSelectionCount = colDef?.cellEditorParams?.maxSelectionCount ?? 1;
        const isMultiSelect = maxSelectionCount !== 1;
        const normalizedAllowedValues = this.getNormalizedAllowedValues(colDef, allowedValues, allowedValueKey);
        if (Array.isArray(value)) {
            return value.some(v => !this.isAllowedDropdownValue(v, normalizedAllowedValues, allowedValueKey));
        }
        if ((memberType === 'multiSelectDropdown' || memberType === 'objectDropdown') && isMultiSelect) {
            const selectedValues: any[] = String(value).split(';').map(v => v.trim());
            return selectedValues.some(v => !this.isAllowedDropdownValue(v, normalizedAllowedValues, allowedValueKey));
        }
        return !this.isAllowedDropdownValue(value, normalizedAllowedValues, allowedValueKey);
    }

    private isAllowedDropdownValue(value: any, normalizedAllowedValues: Set<string>, allowedValueKey?: string): boolean {
        if (value == null || value === '') {
            return true;
        }
        // A reference placeholder for a NULL FK (e.g. StrategyWave id -1) arrives without its display key.
        if (allowedValueKey && typeof value === 'object' && value[allowedValueKey] == null &&
            typeof value.id === 'number' && value.id < 1) {
            return true;
        }
        const normalizedValue = this.normalizeDropdownComparableValue(value, allowedValueKey);
        return normalizedAllowedValues.has(normalizedValue);
    }

    private getNormalizedAllowedValues(colDef: ColDef, allowedValues: any[], allowedValueKey?: string): Set<string> {
        const cacheKey = colDef as unknown as object;
        const cached = this.dropdownAllowedValuesCache.get(cacheKey);
        if (cached && cached.allowedValuesRef === allowedValues && cached.allowedValueKey === allowedValueKey) {
            return cached.normalizedValues;
        }
        const isListManagerStructure = !!(
            colDef?.cellEditorParams?.isListManagerStructure || colDef?.cellRendererParams?.isListManagerStructure
        );
        const normalizedValues = new Set<string>();
        for (const option of allowedValues) {
            let comparable: any = option;
            if (isListManagerStructure) {
                comparable = option?.label;
            } else if (allowedValueKey && option && typeof option === 'object') {
                comparable = option[allowedValueKey];
            }
            // A blank display value extracted from a real DB-backed reference option (e.g. Plating
            // id=41 -> plating: "") is still a legitimate, distinct allowedValues entry and must be
            // included, not treated as a missing/placeholder comparable.
            if (comparable != null) {
                normalizedValues.add(String(comparable).trim());
            }
        }
        this.dropdownAllowedValuesCache.set(cacheKey, {
            allowedValuesRef: allowedValues,
            allowedValueKey,
            normalizedValues
        });
        return normalizedValues;
    }

    private normalizeDropdownComparableValue(value: any, allowedValueKey?: string): string {
        if (value && typeof value === 'object' && allowedValueKey) {
            value = value[allowedValueKey];
        }
        return String(value).trim();
    }

    private shouldSuppressMalformedKeyboardEvent(event: KeyboardEvent | undefined): boolean {
        if (!event) {
            return false;
        }
        return typeof (event as any).key !== 'string';
    }

    storeScrollPosition(): { verticalScrollTop: number, horizontalScrollLeft: number } | null {
        if (!this.gridApi) {
            return null;
        }

        const gridBody: Element = document.querySelector('.ag-body-viewport'); // Main scrollable container
        const gridHeader: Element = document.querySelector('.ag-header-viewport'); // Horizontal scroll container

        return {
            verticalScrollTop: gridBody ? gridBody.scrollTop : 0,
            horizontalScrollLeft: gridHeader ? gridHeader.scrollLeft : 0
        };
    }

    // Restore the scroll position after updating data
    restoreScrollPosition(scrollData: { verticalScrollTop: number, horizontalScrollLeft: number } | null): void {
        if (scrollData) {
            const gridBody: Element = document.querySelector('.ag-body-viewport'); // Main scrollable container
            const gridHeader: Element = document.querySelector('.ag-header-viewport'); // Horizontal scroll container

            if (gridBody) {
                gridBody.scrollTop = scrollData.verticalScrollTop || 0;
            }
            if (gridHeader) {
                gridHeader.scrollLeft = scrollData.horizontalScrollLeft || 0;
            }
        }
    }

    protected setRowData(rows: any[]): void {
        this.rowData = rows || [];
        this.rowDataSignal.set([...(this.rowData || [])]);
    }

    protected syncRowDataSignal(): void {
        this.rowDataSignal.set([...(this.rowData || [])]);
    }

// this method takes the latest data and refreshes the grid to reflect that
    refreshGrid(rows: ValidationModel[], node?: RowNode, onCompleteCallback?: () => void): void {
        const currentScrollPosition = this.storeScrollPosition();
        const nextRows = [...(rows || [])];
        this.setRowData(nextRows);
        if (this.gridApi && !this.gridApi.isDestroyed()) {
            const api: any = this.gridApi as any;
            if (typeof api.setGridOption === 'function') {
                api.setGridOption('rowData', nextRows);
            } else if (typeof api.setRowData === 'function') {
                api.setRowData(nextRows);
            }
            this.restoreScrollPosition(currentScrollPosition);
        }
        this.updateDataChanged(nextRows);
        if (onCompleteCallback) {
            onCompleteCallback();
        }
    }

    private updateSingleRowChangedState(row: ValidationModel): void {
        row.changes?.clear();
        if (row.state === 'new' || row.state === 'deleted' || !row.originalRowData) {
            return;
        }

        for (const col of this.getColsFromColDefOrColGroupDef(this.cols)) {
            if (!col.field || (col as any).esciDerivedGroupChildField) {
                continue;
            }
            let newValue = CommonHelperService.getDeepValue(row, col.field) ?? '';
            let oldValue = CommonHelperService.getDeepValue(row.originalRowData, col.field) ?? '';
            if (col.valueFormatter && col.valueFormatter instanceof Function) {
                newValue = col.valueFormatter({value: newValue} as any);
                oldValue = col.valueFormatter({value: oldValue} as any);
            }
            if (col.field.endsWith('FileId') || col.field.endsWith('ImageIds')) {
                if (newValue === 0) {
                    newValue = -1;
                }
                if (oldValue === 0) {
                    oldValue = -1;
                }
            }
            if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                if (oldValue instanceof Date) {
                    if (isNaN(oldValue.getTime())) {
                        oldValue = '';
                    } else {
                        oldValue = oldValue.toISOString().split('T')[0];
                    }
                }
                // If this cell is a user field, then we want to convert this into a human-readable name.
                // To test for a user field, we can test if (col.cellRenderer.name === 'UserSearchCellEditorComponent')
                // but that approach is fragile (it might be '_UserSearchCellEditorComponent'), so we check for the
                // presence of a "userField" member in col.cellRendererParams (which could have a value like 'dre'):
                if (oldValue instanceof Object && col.cellRendererParams && col.cellRendererParams.userField) {
                    oldValue = this.getValueFromUser(oldValue);
                }
                if (col.field.endsWith('FileId') || col.field.endsWith('ImageIds')) {
                    let type = col.field.includes('attachment') ? 'file' : 'image';
                    if ((typeof oldValue === 'string' && oldValue.includes(';')) ||
                        (typeof newValue === 'string' && newValue.includes(';'))) {
                        type += 's';
                    }
                    if (newValue === -1) {
                        row.changes?.set(col.field, type + ' removed.');
                    } else if (oldValue === -1) {
                        row.changes?.set(col.field, type + ' added.');
                    } else {
                        row.changes?.set(col.field, type + ' changed.');
                    }
                } else {
                    row.changes?.set(col.field, 'Original Value: ' + oldValue);
                }
            }
        }

        row.state = (row.changes.size > 0) ? 'changed' : 'saved';
    }

    // when user stops editing a cell, automatically validate and highlight modified rows
    cellEditingStopped(event: CellEditingStoppedEvent<any>, rows: ValidationModel[]) {
        // Update the edited row class immediately so color/style changes are visible right away.
        // Do not short-circuit on event value equality ("if (event.value === event.oldValue)"): dropdown editors can
        // report identical event values while still mutating row data, and we must keep Save/tooltip/styling in sync.
        this.updateSingleRowChangedState(event.data as ValidationModel);
        if (event.node && this.gridApi && !this.gridApi.isDestroyed() && event.column) {
            refreshCellAndDeferredRowRedraw(this.gridApi, event.node, event.column);
        }
        // Keep Save/Reset buttons responsive even if ag-grid event row reference differs from bound rowData array.
        this.dataChanged = (rows?.some(r => r?.state !== 'saved') ?? false) ||
                           ((event.data as ValidationModel)?.state !== 'saved');

        const editedRow = event?.data as ValidationModel | undefined;
        const editedColDef = event?.column?.getColDef?.();
        const editedFieldName = editedColDef?.field;
        if (this.validateOnlyEditedCellOnCellEdit() && editedRow && editedFieldName) {
            this.validateEditedCellOnCellEdit(editedRow, editedFieldName, editedColDef, rows);
            return;
        }
        const rowsToValidate = this.validateOnlyEditedRowOnCellEdit() && editedRow ? [editedRow] : rows;
        this.areRowsValid(rowsToValidate);
        // Do not call refreshGrid here: replacing rowData resets focus (e.g. Tab to next cell jumps to row 0).
        // refreshCellAndDeferredRowRedraw updates the stopped cell and redraws the row once no edit is active.
    }

    protected validateOnlyEditedCellOnCellEdit(): boolean {
        return false;
    }

    protected validateOnlyEditedRowOnCellEdit(): boolean {
        return false;
    }

    private validateEditedCellOnCellEdit(
        row: ValidationModel,
        fieldName: string,
        colDef: ColDef | undefined,
        rows: ValidationModel[]
    ): void {
        this.messageService.clear();

        const existingRowErrors = (row as any)._cellValidationErrors as Map<string, string> | undefined;
        const rowErrors = existingRowErrors ? new Map(existingRowErrors) : new Map<string, string>();
        rowErrors.delete(fieldName);

        const editedFieldRule = (row.fieldRules || []).find(fieldRule => fieldRule.fieldName === fieldName);
        if (editedFieldRule) {
            const fieldMessage = this.validatorService.validateField(editedFieldRule, row, true);
            if (fieldMessage) {
                rowErrors.set(fieldName, fieldMessage);
                this.validatorService.validateFields(
                    [row],
                    new Map<ValidationModel, Map<string, string>>([[row, new Map<string, string>([[fieldName, fieldMessage]])]])
                );
            }
            if (editedFieldRule.uniqueFieldSetName) {
                const activeRows = (rows || []).filter(r => r?.state !== 'deleted');
                if (activeRows.some(r => this.validatorService.checkDuplicate(r, row))) {
                    this.duplicate = true;
                }
            }
        }

        if (colDef && this.hasInvalidDropdownSelection(row, colDef)) {
            rowErrors.set(fieldName, 'Value is not in the allowed list.');
        }

        if (rowErrors.size > 0) {
            (row as any)._cellValidationErrors = rowErrors;
        } else {
            delete (row as any)._cellValidationErrors;
        }
        this.recomputeEditedCellValidationFlags(rows);
    }

    private recomputeEditedCellValidationFlags(rows: ValidationModel[]): void {
        const activeRows = (rows || []).filter(r => r?.state !== 'deleted');
        this.rowsInvalid = activeRows.some(r => {
            const rowErrors = (r as any)._cellValidationErrors as Map<string, string> | undefined;
            return rowErrors instanceof Map && rowErrors.size > 0;
        });
        this.duplicate = this.hasDuplicateRows(activeRows);
    }

    private hasDuplicateRows(rows: ValidationModel[]): boolean {
        for (let i = 0; i < rows.length; i++) {
            for (let j = i + 1; j < rows.length; j++) {
                if (this.areRowsDuplicateByUniqueFieldSets(rows[i], rows[j])) {
                    return true;
                }
            }
        }
        return false;
    }

    private areRowsDuplicateByUniqueFieldSets(left: ValidationModel, right: ValidationModel): boolean {
        const uniqueSetRules = this.getUniqueFieldRulesBySet(left);
        if (uniqueSetRules.size === 0) {
            return false;
        }
        for (const rules of uniqueSetRules.values()) {
            if (rules.every(rule => this.areFieldValuesEqualForDuplicateCheck(left, right, rule))) {
                return true;
            }
        }
        return false;
    }

    private getUniqueFieldRulesBySet(row: ValidationModel): Map<string, FieldRule[]> {
        const rulesBySet = new Map<string, FieldRule[]>();
        for (const rule of row?.fieldRules || []) {
            if (!rule.uniqueFieldSetName) {
                continue;
            }
            const bucket = rulesBySet.get(rule.uniqueFieldSetName) || [];
            bucket.push(rule);
            rulesBySet.set(rule.uniqueFieldSetName, bucket);
        }
        return rulesBySet;
    }

    private areFieldValuesEqualForDuplicateCheck(left: ValidationModel, right: ValidationModel, rule: FieldRule): boolean {
        const leftValue = CommonHelperService.getDeepValue(left, rule.fieldName);
        const rightValue = CommonHelperService.getDeepValue(right, rule.fieldName);
        if (leftValue === rightValue) {
            return true;
        }
        if (rule.type === 'number') {
            if (leftValue === null || leftValue === undefined || leftValue === '' ||
                rightValue === null || rightValue === undefined || rightValue === '') {
                return false;
            }
            const leftNumber = Number(leftValue);
            const rightNumber = Number(rightValue);
            return !Number.isNaN(leftNumber) && !Number.isNaN(rightNumber) && leftNumber === rightNumber;
        }
        if (rule.type === 'string') {
            return (leftValue?.toUpperCase() || '') === (rightValue?.toUpperCase() || '');
        }
        return false;
    }

    updateDataChanged(rows: ValidationModel[]) {
        for (const row of rows || []) {
            this.updateSingleRowChangedState(row);
        }
        // set this.dataChanged based on if any row in row has a non-saved value (new/deleted/changed)
        this.dataChanged = rows?.some(row => row['state'] !== 'saved');
    }

    // This method applies a style to any custom multi select floating filter so that it will resize with the column
    // without this, the floating filter will not fully expand horizontally for some reason
    setupCustomFilterStyling() {
        const pinnedIndexesList = [];
        const indexesList = [];
        let pinnedCount = 0;
        // Get a list of all indexes of columns that use a custom multi-select filter
        this.getColsFromColDefOrColGroupDef(this.cols).forEach((col, index) => {
            const pinnedLeft = col.pinned === 'left';
            if (pinnedLeft) {
                pinnedCount++;
            }
            if (col.floatingFilterComponent === CustomMultiSelectFloatingFilterComponent) {
                if (pinnedLeft) {
                    pinnedIndexesList.push(index);
                } else {
                    indexesList.push(index);
                }
            }
        });
        // loop through indexesList and use these for the nth-child reference
        pinnedIndexesList.forEach(index => {
            EditableGridComponent.applyStyleToFilterColumns(index, '.ag-pinned-left-header');
        });
        indexesList.forEach(index => {
            EditableGridComponent.applyStyleToFilterColumns(index - pinnedCount, '.ag-header-viewport');
        });
    }

    setDropdownOption(optionsList: string[], field: string) {
        this.getColsFromColDefOrColGroupDef(this.cols).forEach(col => {
            if (col.field === field) {
                col.cellEditor = TypeaheadCellEditorComponent;
                col.cellEditorParams = {values: optionsList, editable: false};
                return;
            }
        });
    }

    onDeleteClick(params: any, dataArray: any[], refreshCallback: (updatedData: any[]) => void): void {
        if (params.data.id === -1) {
            const index = dataArray.indexOf(params.data);
            if (index >= 0) {
                dataArray.splice(index, 1);
            }
        } else {
            params.data.state = 'deleted';
        }
        refreshCallback([...dataArray]);
    }

    /**
     * Resolves the AG Grid pagination bar for a specific grid host. Prefer this over
     * {@link document.querySelector} when multiple grids exist on the page.
     */
    protected getPaginationPanelForGrid(gridRoot: HTMLElement | null | undefined): HTMLElement | null {
        if (!gridRoot) {
            return null;
        }
        return gridRoot.querySelector('.ag-paging-panel');
    }

    /**
     * Builds the flex row container used for custom footer text (label + value) in the AG Grid
     * pagination bar. Does not attach to the DOM; use {@link insertNodeIntoAgGridPaginationPanel}.
     */
    createCustomFooterLabelTextContainer(label: string, text: string): HTMLElement {
        const customLabel = document.createElement('span');
        customLabel.innerText = label;
        customLabel.style.fontWeight = '700';

        const customText = document.createElement('span');
        customText.style.fontWeight = '600';
        customText.innerText = text;
        customText.style.paddingLeft = '6px';

        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'flex-start';
        container.style.flex = '1';

        container.appendChild(customLabel);
        container.appendChild(customText);
        return container;
    }

    /**
     * Inserts a node at the start of the AG Grid pagination row (left-aligned flex area).
     * When {@link gridRoot} is omitted, uses the first `.ag-paging-panel` in the document.
     */
    insertNodeIntoAgGridPaginationPanel(node: Node, gridRoot?: HTMLElement | null): void {
        afterNextRender(() => {
            const paginationPanel = (gridRoot != null
                ? this.getPaginationPanelForGrid(gridRoot)
                : document.querySelector('.ag-paging-panel')) as HTMLElement | null;
            if (paginationPanel) {
                paginationPanel.insertBefore(node, paginationPanel.childNodes[0]);
            }
        }, { injector: this.injector });
    }

    /**
     * Builds a primary-style row action button (label + click handler) in a flex container suitable
     * for {@link insertNodeIntoAgGridPaginationPanel}.
     */
    createAddRowButtonContainer(label: string, title: string, onClick: () => void): HTMLElement {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'flex-start';
        container.style.flex = '1';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = label;
        btn.title = title;
        btn.className = 'ag-grid-add-row-pagination-btn';
        btn.addEventListener('click', () => onClick());

        container.appendChild(btn);
        return container;
    }

    /**
     * Prepends label + text to the pagination row. When {@link gridRoot} is omitted, uses the first
     * `.ag-paging-panel` in the document (legacy behavior).
     */
    addCustomFooterToAgGrid(label: string, text: string, gridRoot?: HTMLElement | null) {
        const container = this.createCustomFooterLabelTextContainer(label, text);
        this.insertNodeIntoAgGridPaginationPanel(container, gridRoot);
    }

    getColsFromColDefOrColGroupDef(colDefs: (ColDef | ColGroupDef)[]): ColDef[] {
        return colDefs.reduce<ColDef[]>((acc, colDef) => {
            if ('children' in colDef) {
                acc.push(...this.getColsFromColDefOrColGroupDef(colDef.children));
            } else {
                acc.push(colDef);
            }
            return acc;
        }, []);
    }

    fetchPersistentUserData() {
        this.userService.fetchPersistentUserData().subscribe({
            next: (data: any) => {
                if (typeof data === 'object') {
                    this.persistentUserData = data;
                    this.onUserDataLoaded();
                } else {
                    console.error('Error getting UserData: ' + data);
                    this.messageService.processError('Error getting UserData: ' + data);
                }
            },
            error: (err: any) => {
                const errorMessage = 'Error getting UserData: ' + (err?.error?.message || err?.message || err);
                console.error(errorMessage);
                this.messageService.processError(errorMessage);
            }
        });
    }

    onUserDataLoaded() {
        // override this method in the component to do something with the data
    }
}
